import React from 'react'
import { View } from 'react-native'

import SkeletonLoader from '@common/components/SkeletonLoader'
import Text from '@common/components/Text'
import { useTranslation } from '@common/config/localization'
import useTheme from '@common/hooks/useTheme'
import { useRailgunOverviewBalance } from '@common/modules/dashboard/hooks/useRailgunOverviewBalance'
import spacings from '@common/styles/spacings'
import { THEME_TYPES } from '@common/styles/themeConfig'
import { Mode, ModeEnum } from '@web/contexts/appModeContext'
import { openInTab } from '@web/extension-services/background/webapi/tab'
import { WEB_ROUTES } from '@common/modules/router/constants/common'
import { getUiType } from '@web/utils/uiType'

const { isPopup } = getUiType()
const BATCH_STATUS_MAX_WIDTH = 260
const BATCH_STATUS_TAB_MAX_WIDTH = 300

const stylesInline = {
  hintContainer: {
    height: 28,
    justifyContent: 'center',
    alignSelf: 'flex-start',
    paddingVertical: 1
  } as const,
  hintText: { lineHeight: 14 } as const
}

type Props = {
  mode: Mode
}

const BatchOrFirstBalanceHint = ({ mode }: Props) => {
  const { t } = useTranslation()
  const { theme, themeType } = useTheme()
  const railgunOverview = useRailgunOverviewBalance(mode)
  const maxWidth = isPopup ? BATCH_STATUS_MAX_WIDTH : BATCH_STATUS_TAB_MAX_WIDTH

  const handleOpenInTab = async () => {
    await openInTab({
      url: `tab.html#/${WEB_ROUTES.dashboard}?mode=${ModeEnum.PRIVATE}`
    })
  }

  if (mode !== ModeEnum.PRIVATE) return null
  if (!railgunOverview.isRailgunBalanceFetching) return null

  const textColor =
    themeType === THEME_TYPES.DARK ? theme.primaryBackgroundInverted : theme.primaryBackground
  const linkColor = textColor

  if (railgunOverview.showFirstBalanceLoadHint) {
    return (
      <View style={[stylesInline.hintContainer, spacings.mrTy]}>
        <Text
          fontSize={isPopup ? 10 : 12}
          numberOfLines={2}
          style={(stylesInline.hintText, { maxWidth })}
          color={textColor}
        >
          {isPopup ? (
            <>
              {t('Initial load may take up to 15 minutes. Use expanded mode to avoid issues.')}{' '}
              <Text
                appearance="linkText"
                fontSize={10}
                onPress={handleOpenInTab}
                style={{
                  color: linkColor,
                  fontWeight: '600',
                  textDecorationLine: 'underline',
                  // @ts-ignore
                  textDecorationColor: linkColor,
                  // @ts-ignore
                  textUnderlineOffset: 2
                }}
              >
                {t('Open in full screen')}
              </Text>
            </>
          ) : (
            t('Please allow up to 15 minutes for the initial load')
          )}
        </Text>
      </View>
    )
  }

  return (
    <SkeletonLoader lowOpacity width={100} height={28} borderRadius={8} style={spacings.mrTy} />
  )
}

export default React.memo(BatchOrFirstBalanceHint)
